import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase';
import {
  collection, query, orderBy, onSnapshot, addDoc, getDocs, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';

const CHAT_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/chatWithClient';

// One continuous, team-shared thread per client. Messages live in
// clients/{clientId}/chat and persist across visits — leaving the profile does
// not end the conversation.
export default function ClientChat({ clientId, client }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const businessName = client?.businessName || 'this client';

  useEffect(() => {
    const q = query(collection(db, 'clients', clientId, 'chat'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [clientId]);

  // Keep the transcript pinned to the newest message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const writeMessage = (role, content) =>
    addDoc(collection(db, 'clients', clientId, 'chat'), {
      role,
      content,
      author: role === 'user' ? (auth.currentUser?.email || 'unknown') : 'Claude',
      createdAt: serverTimestamp(),
    });

  // Shared call path for both free-form chat and the audit button.
  const runTurn = async ({ userText, mode, history }) => {
    setError('');
    setSending(true);
    try {
      await writeMessage('user', userText);
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ clientId, mode, messages: history }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.details || data.error || 'Request failed');
      await writeMessage('assistant', data.reply);
    } catch (e) {
      console.error('chatWithClient failed:', e);
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    const history = [
      ...messages.map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: text },
    ];
    runTurn({ userText: text, mode: 'chat', history });
  };

  const audit = () => {
    if (sending) return;
    runTurn({
      userText: `🔍 Audit this profile — confirm all data belongs to ${businessName}.`,
      mode: 'audit',
      history: [],
    });
  };

  const clearThread = async () => {
    if (!window.confirm('Start a new conversation? This permanently clears the current thread for this client.')) return;
    const snap = await getDocs(collection(db, 'clients', clientId, 'chat'));
    await Promise.all(snap.docs.map(d => deleteDoc(doc(db, 'clients', clientId, 'chat', d.id))));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Ask Claude</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-sm btn-secondary"
            style={{ fontSize: 11 }}
            onClick={audit}
            disabled={sending}
            title={`Check that every document and the brand voice belong to ${businessName}`}
          >
            {sending ? '…' : '🔍 Audit this profile'}
          </button>
          {messages.length > 0 && (
            <button
              className="btn btn-sm btn-secondary"
              style={{ fontSize: 11, opacity: 0.7 }}
              onClick={clearThread}
              disabled={sending}
              title="Clear this thread and start fresh"
            >
              New conversation
            </button>
          )}
        </div>
      </div>

      {/* Transcript */}
      <div
        ref={scrollRef}
        style={{ maxHeight: 460, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: messages.length ? '4px 2px 8px' : 0 }}
      >
        {messages.length === 0 && !sending && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'center', padding: '18px 12px', lineHeight: 1.6 }}>
            Ask anything about {businessName} using their uploaded documents and brand voice.<br />
            Or run <strong>Audit this profile</strong> to confirm every file belongs to this client.
          </div>
        )}

        {messages.map(m => {
          const isUser = m.role === 'user';
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 3, padding: '0 4px' }}>
                {isUser ? (m.author || 'You') : 'Claude'}
              </div>
              <div style={{
                maxWidth: '88%',
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                padding: '9px 12px',
                borderRadius: 10,
                background: isUser ? 'var(--accent-blue-bg)' : 'var(--bg-primary)',
                border: '1px solid',
                borderColor: isUser ? 'var(--accent-blue)' : 'var(--border)',
                color: 'var(--text-primary)',
              }}>
                {m.content}
              </div>
            </div>
          );
        })}

        {sending && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 4px', fontStyle: 'italic' }}>Claude is thinking…</div>
        )}
      </div>

      {error && <div style={{ fontSize: 12, color: '#f87171', margin: '8px 0' }}>{error}</div>}

      {/* Composer */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <textarea
          placeholder={`Ask about ${businessName}…`}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          style={{ flex: 1, minHeight: 44, maxHeight: 140, fontSize: 13, lineHeight: 1.5, resize: 'vertical' }}
        />
        <button className="btn btn-primary" onClick={send} disabled={sending || !input.trim()} style={{ alignSelf: 'stretch' }}>
          {sending ? '…' : 'Send'}
        </button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
        Shared thread — saved per client and visible to the whole team. Enter to send, Shift+Enter for a new line.
      </div>
    </div>
  );
}
